import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Trash2, X, AlertCircle, CheckCircle, Edit3, List, CalendarDays } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

interface Subject {
  id: string;
  name: string;
  onlineRate: number;
  inPersonRate: number;
}

type AvailabilityType = 'LESSON' | 'CONSULTATION';

interface Availability {
  id: string;
  dayOfWeek: number;
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  subject?: Subject | null;
  availabilityType?: AvailabilityType;
}

// Monday-first week ordering. Backend uses Sunday=0, so we translate via indexes.
// gridDayIndex 0..6 maps to Monday..Sunday
const MON_FIRST_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MON_FIRST_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Backend day-of-week indices (Sunday=0) for each visual column
const BACKEND_DOW_FOR_GRID: number[] = [1, 2, 3, 4, 5, 6, 0];

const GRID_START_HOUR = 7;  // 07:00
const GRID_END_HOUR = 23;   // 23:00 (exclusive — last row starts at 22:30)
const SLOT_MINUTES = 30;
const ROWS_PER_HOUR = 60 / SLOT_MINUTES;
const TOTAL_ROWS = (GRID_END_HOUR - GRID_START_HOUR) * ROWS_PER_HOUR;
// Pixel height per 30-minute slot row. The hour-label column uses
// ROWS_PER_HOUR * ROW_PX so labels and day-column cells stay aligned —
// previously the labels were 60px each while rows were 16px each, so the
// label column extended ~420px below the actual clickable grid and any
// availability set in the late-evening hours had no cells to click.
const ROW_PX = 30;

// Palette tones derived from the EJL tokens. Ordered as a tasteful rotation.
// Each entry has a background swatch and a text color for labels.
const SUBJECT_TONES = [
  { bg: 'rgb(var(--ochre) / 0.22)', text: 'rgb(var(--ink))', border: 'rgb(var(--ochre) / 0.55)' },
  { bg: 'rgb(var(--navy) / 0.14)',  text: 'rgb(var(--ink))', border: 'rgb(var(--navy) / 0.45)' },
  { bg: 'rgb(var(--ochre) / 0.38)', text: 'rgb(var(--ink))', border: 'rgb(var(--ochre) / 0.70)' },
  { bg: 'rgb(var(--ink) / 0.10)',   text: 'rgb(var(--ink))', border: 'rgb(var(--ink) / 0.35)' },
  { bg: 'rgb(var(--muted) / 0.30)', text: 'rgb(var(--ink))', border: 'rgb(var(--muted) / 0.65)' },
];
const CONSULTATION_TONE = { bg: 'rgb(var(--navy) / 0.22)', text: 'rgb(var(--ink))', border: 'rgb(var(--navy) / 0.55)' };

// Deterministic hash: simple sum of char codes modded into palette length.
const subjectTone = (subjectId?: string | null) => {
  if (!subjectId) return SUBJECT_TONES[4]; // "All subjects" uses the muted tone
  let h = 0;
  for (let i = 0; i < subjectId.length; i++) h = (h * 31 + subjectId.charCodeAt(i)) >>> 0;
  return SUBJECT_TONES[h % SUBJECT_TONES.length];
};

// "HH:MM" helpers
const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
};
const fromMinutes = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const rowToMinutes = (row: number) => GRID_START_HOUR * 60 + row * SLOT_MINUTES;
const minutesToRow = (mins: number) => (mins - GRID_START_HOUR * 60) / SLOT_MINUTES;

const formatTime12 = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${period}`;
};

const gridColFromBackendDow = (dow: number): number => {
  // dow 0=Sun..6=Sat, we need col where Mon=0..Sun=6
  return BACKEND_DOW_FOR_GRID.indexOf(dow);
};

// --- Mobile detection ---
const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const check = () => setNarrow(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return narrow;
};

interface SlotFormData {
  id: string | null;
  gridCol: number;        // 0..6 Mon-first
  startTime: string;      // "HH:MM"
  endTime: string;        // "HH:MM"
  subjectId: string;      // "" = all subjects
  availabilityType: AvailabilityType;
}

const blankForm = (gridCol = 0, startTime = '09:00', endTime = '10:00'): SlotFormData => ({
  id: null,
  gridCol,
  startTime,
  endTime,
  subjectId: '',
  availabilityType: 'LESSON',
});

const TutorAvailabilityManager: React.FC = () => {
  const { user } = useAuth();
  const isNarrow = useIsNarrow();

  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<SlotFormData>(blankForm());

  // Drag-to-create state (desktop grid only)
  const [dragState, setDragState] = useState<{
    col: number;
    startRow: number;
    currentRow: number;
  } | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [availabilityResponse, subjectsResponse] = await Promise.all([
        api.get('/availability/tutor'),
        api.get('/subjects'),
      ]);
      setAvailabilities(Array.isArray(availabilityResponse.data) ? availabilityResponse.data : []);
      setSubjects(Array.isArray(subjectsResponse.data) ? subjectsResponse.data : []);
    } catch (err) {
      console.error('Failed to fetch availability data:', err);
      setError('Failed to load availability data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const flashSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  // --- CRUD ---
  const saveSlot = async (payload: SlotFormData) => {
    const body = {
      dayOfWeek: BACKEND_DOW_FOR_GRID[payload.gridCol],
      startTime: payload.startTime,
      endTime: payload.endTime,
      subjectId: payload.subjectId || null,
      availabilityType: payload.availabilityType,
    };
    if (payload.id) {
      await api.put(`/availability/${payload.id}`, body);
    } else {
      await api.post('/availability/tutor', body);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (toMinutes(form.startTime) >= toMinutes(form.endTime)) {
      setError('End time must be after start time.');
      return;
    }
    const backendDow = BACKEND_DOW_FOR_GRID[form.gridCol];
    const startMin = toMinutes(form.startTime);
    const endMin = toMinutes(form.endTime);
    const overlap = availabilities.find(a =>
      a.id !== form.id &&
      a.dayOfWeek === backendDow &&
      startMin < toMinutes(a.endTime) &&
      endMin > toMinutes(a.startTime)
    );
    if (overlap) {
      setError(`Overlaps existing slot ${formatTime12(overlap.startTime)}–${formatTime12(overlap.endTime)}.`);
      return;
    }
    try {
      await saveSlot(form);
      setModalOpen(false);
      setForm(blankForm());
      await fetchData();
      flashSuccess(form.id ? 'Slot updated.' : 'Slot added.');
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save slot.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this availability slot?')) return;
    try {
      await api.delete(`/availability/${id}`);
      await fetchData();
      flashSuccess('Slot deleted.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete slot.');
    }
  };

  const openAddModal = (preset?: Partial<SlotFormData>) => {
    setForm({ ...blankForm(), ...preset });
    setError('');
    setModalOpen(true);
  };

  const openEditModal = (slot: Availability) => {
    setForm({
      id: slot.id,
      gridCol: gridColFromBackendDow(slot.dayOfWeek),
      startTime: slot.startTime.slice(0, 5),
      endTime: slot.endTime.slice(0, 5),
      subjectId: slot.subject?.id || '',
      availabilityType: slot.availabilityType || 'LESSON',
    });
    setError('');
    setModalOpen(true);
  };

  // --- Derived: positions of slots on grid ---
  const slotsByCol = useMemo(() => {
    const byCol: Availability[][] = Array.from({ length: 7 }, () => []);
    availabilities.forEach(a => {
      const col = gridColFromBackendDow(a.dayOfWeek);
      if (col >= 0) byCol[col].push(a);
    });
    byCol.forEach(list => list.sort((a, b) => a.startTime.localeCompare(b.startTime)));
    return byCol;
  }, [availabilities]);

  // --- Desktop grid interactions ---
  const handleCellMouseDown = (col: number, row: number, e: React.MouseEvent) => {
    if (e.button !== 0) return;
    // Don't start a drag from an occupied cell; those are edit clicks
    if (isRowOccupied(col, row)) return;
    setDragState({ col, startRow: row, currentRow: row });
  };

  const handleCellMouseEnter = (col: number, row: number) => {
    if (!dragState || dragState.col !== col) return;
    if (isRowOccupied(col, row)) return;
    setDragState({ ...dragState, currentRow: row });
  };

  const handleGridMouseUp = () => {
    if (!dragState) return;
    const startRow = Math.min(dragState.startRow, dragState.currentRow);
    const endRow = Math.max(dragState.startRow, dragState.currentRow) + 1; // inclusive end
    const startTime = fromMinutes(rowToMinutes(startRow));
    const endTime = fromMinutes(rowToMinutes(endRow));
    const col = dragState.col;
    setDragState(null);
    openAddModal({ gridCol: col, startTime, endTime });
  };

  useEffect(() => {
    if (!dragState) return;
    const up = () => handleGridMouseUp();
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState]);

  const isRowOccupied = (col: number, row: number): boolean => {
    const rowStartMin = rowToMinutes(row);
    const rowEndMin = rowStartMin + SLOT_MINUTES;
    return slotsByCol[col].some(s =>
      rowStartMin < toMinutes(s.endTime) && rowEndMin > toMinutes(s.startTime)
    );
  };

  // Hours labels on the left axis
  const hourLabels = useMemo(() => {
    const labels: string[] = [];
    for (let h = GRID_START_HOUR; h < GRID_END_HOUR; h++) {
      labels.push(`${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`);
    }
    return labels;
  }, []);

  // Build a list view (sorted by day then time)
  const sortedList = useMemo(() => {
    const flat: Array<Availability & { gridCol: number }> = availabilities.map(a => ({
      ...a,
      gridCol: gridColFromBackendDow(a.dayOfWeek),
    }));
    flat.sort((a, b) => a.gridCol - b.gridCol || a.startTime.localeCompare(b.startTime));
    return flat;
  }, [availabilities]);

  if (!user || user.role !== 'TUTOR') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-cream border border-rule rounded-card p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-ochre mr-3" />
          <p className="text-ink">Access denied. Tutor role required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-h3 font-serif text-ink mb-1">Weekly availability</h2>
          <p className="text-muted text-sm">
            Click and drag on an empty cell to create a slot, or click an existing slot to edit.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openAddModal()}
          className="btn btn-primary rounded-card inline-flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add slot
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="mb-4 bg-cream border border-rule rounded-card p-3 flex items-center">
          <CheckCircle className="w-4 h-4 text-ochre mr-2" />
          <p className="text-sm text-ink">{success}</p>
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-card p-3 flex items-center">
          <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
          <p className="text-sm text-red-800 flex-1">{error}</p>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="bg-paper border border-rule rounded-card p-8">
          <div className="animate-pulse space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-6 bg-cream rounded" />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          {/* Grid or mobile list */}
          {!isNarrow ? (
            <div
              ref={gridRef}
              className="bg-paper border border-rule rounded-card overflow-hidden select-none"
              onMouseLeave={() => {
                if (dragState) handleGridMouseUp();
              }}
            >
              {/* Column headers */}
              <div className="grid" style={{ gridTemplateColumns: '56px repeat(7, minmax(0, 1fr))' }}>
                <div className="bg-cream border-b border-r border-rule" />
                {MON_FIRST_SHORT.map((label, i) => (
                  <div
                    key={i}
                    className="bg-cream border-b border-r border-rule last:border-r-0 py-2 text-center"
                  >
                    <div className="text-sm font-medium text-ink">{label}</div>
                  </div>
                ))}
              </div>

              {/* Body: left axis + 7 day columns */}
              <div
                className="grid"
                style={{ gridTemplateColumns: '56px repeat(7, minmax(0, 1fr))' }}
              >
                {/* Left axis: hours */}
                <div className="border-r border-rule">
                  {hourLabels.map((label, i) => (
                    <div
                      key={i}
                      className="border-b border-rule text-[11px] text-muted text-right pr-2 flex items-start justify-end"
                      style={{ height: `${ROWS_PER_HOUR * ROW_PX}px` }}
                    >
                      <span className="relative -top-1.5">{label}</span>
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {Array.from({ length: 7 }).map((_, col) => (
                  <div
                    key={col}
                    className="relative border-r border-rule last:border-r-0"
                    style={{ height: `${TOTAL_ROWS * ROW_PX}px` }}
                  >
                    {/* Half-hour cells (clickable) */}
                    {Array.from({ length: TOTAL_ROWS }).map((_, row) => {
                      const isHourMark = row % 2 === 0;
                      const occupied = isRowOccupied(col, row);
                      const inDrag =
                        dragState &&
                        dragState.col === col &&
                        row >= Math.min(dragState.startRow, dragState.currentRow) &&
                        row <= Math.max(dragState.startRow, dragState.currentRow);
                      return (
                        <div
                          key={row}
                          onMouseDown={(e) => handleCellMouseDown(col, row, e)}
                          onMouseEnter={() => handleCellMouseEnter(col, row)}
                          className={`${isHourMark ? 'border-t border-rule' : 'border-t border-rule/40'} ${
                            inDrag ? 'bg-ochre/20' : occupied ? '' : 'hover:bg-cream'
                          } cursor-pointer`}
                          style={{ height: `${ROW_PX}px` }}
                        />
                      );
                    })}

                    {/* Filled slot blocks sit absolutely on top */}
                    {slotsByCol[col].map((slot) => {
                      const top = minutesToRow(toMinutes(slot.startTime)) * ROW_PX;
                      const height =
                        (minutesToRow(toMinutes(slot.endTime)) -
                          minutesToRow(toMinutes(slot.startTime))) * ROW_PX;
                      const isConsult = slot.availabilityType === 'CONSULTATION';
                      const tone = isConsult ? CONSULTATION_TONE : subjectTone(slot.subject?.id);
                      return (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() => openEditModal(slot)}
                          className="absolute left-0.5 right-0.5 rounded-[6px] border px-1.5 py-1 text-left overflow-hidden hover:ring-1 hover:ring-ink/40 focus:outline-none focus:ring-2 focus:ring-ochre"
                          style={{
                            top: `${top}px`,
                            height: `${Math.max(height - 2, 18)}px`,
                            backgroundColor: tone.bg,
                            borderColor: tone.border,
                            color: tone.text,
                          }}
                          title={`${formatTime12(slot.startTime)} – ${formatTime12(slot.endTime)}${
                            slot.subject ? ` · ${slot.subject.name}` : ''
                          }${isConsult ? ' · Consultation' : ''}`}
                        >
                          <div className="text-[10px] font-medium leading-tight">
                            {formatTime12(slot.startTime)}
                          </div>
                          <div className="text-[11px] leading-tight truncate">
                            {slot.subject?.name || 'All subjects'}
                            {isConsult ? ' · Consult' : ''}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Mobile list-only view */
            <MobileList
              sortedList={sortedList}
              onEdit={openEditModal}
              onDelete={handleDelete}
              onAdd={openAddModal}
            />
          )}

          {/* Side list (desktop) / full list (mobile shows this too under the mobile list) */}
          {!isNarrow && (
            <div className="bg-paper border border-rule rounded-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-ink inline-flex items-center">
                  <List className="w-4 h-4 mr-2 text-ochre" />
                  Configured slots
                </h3>
                <span className="text-xs text-muted">{sortedList.length} total</span>
              </div>
              {sortedList.length === 0 ? (
                <p className="text-sm text-muted py-8 text-center">
                  No availability configured yet. Click and drag on the grid to add a slot.
                </p>
              ) : (
                <ul className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                  {sortedList.map((slot) => {
                    const isConsult = slot.availabilityType === 'CONSULTATION';
                    const tone = isConsult ? CONSULTATION_TONE : subjectTone(slot.subject?.id);
                    return (
                      <li
                        key={slot.id}
                        className="flex items-start gap-2 p-2 rounded-[6px] border border-rule bg-cream"
                      >
                        <span
                          className="w-1.5 self-stretch rounded-sm"
                          style={{ backgroundColor: tone.border }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-ink">
                            {MON_FIRST_LABELS[slot.gridCol]}
                          </div>
                          <div className="text-xs text-muted">
                            {formatTime12(slot.startTime)} – {formatTime12(slot.endTime)}
                          </div>
                          <div className="text-xs text-ink mt-0.5 truncate">
                            {slot.subject?.name || 'All subjects'}
                            {isConsult && (
                              <span className="ml-1 text-muted">· Consultation</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => openEditModal(slot)}
                            className="p-1 text-muted hover:text-ochre"
                            title="Edit"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(slot.id)}
                            className="p-1 text-muted hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <SlotModal
          form={form}
          setForm={setForm}
          subjects={subjects}
          onClose={() => {
            setModalOpen(false);
            setError('');
          }}
          onSubmit={handleSubmit}
          onDelete={form.id ? () => handleDelete(form.id as string).then(() => setModalOpen(false)) : undefined}
          error={error}
        />
      )}
    </div>
  );
};

// --- Subcomponents ---

interface MobileListProps {
  sortedList: Array<Availability & { gridCol: number }>;
  onEdit: (slot: Availability) => void;
  onDelete: (id: string) => void;
  onAdd: () => void;
}

const MobileList: React.FC<MobileListProps> = ({ sortedList, onEdit, onDelete, onAdd }) => {
  const grouped = useMemo(() => {
    const map: Record<number, Array<Availability & { gridCol: number }>> = {};
    sortedList.forEach(s => {
      if (!map[s.gridCol]) map[s.gridCol] = [];
      map[s.gridCol].push(s);
    });
    return map;
  }, [sortedList]);

  return (
    <div className="bg-paper border border-rule rounded-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-ink inline-flex items-center">
          <CalendarDays className="w-4 h-4 mr-2 text-ochre" />
          Weekly slots
        </h3>
        <button onClick={onAdd} className="text-xs text-ochre underline">Add slot</button>
      </div>
      {sortedList.length === 0 ? (
        <p className="text-sm text-muted py-8 text-center">
          No availability configured yet. Tap “Add slot” to get started.
        </p>
      ) : (
        <div className="space-y-4">
          {MON_FIRST_LABELS.map((label, col) => {
            const list = grouped[col] || [];
            if (list.length === 0) return null;
            return (
              <div key={col}>
                <div className="text-xs uppercase tracking-wide text-muted mb-1">{label}</div>
                <ul className="space-y-2">
                  {list.map((slot) => {
                    const isConsult = slot.availabilityType === 'CONSULTATION';
                    const tone = isConsult ? CONSULTATION_TONE : subjectTone(slot.subject?.id);
                    return (
                      <li
                        key={slot.id}
                        className="flex items-center gap-2 p-2 rounded-[6px] border border-rule bg-cream"
                      >
                        <span
                          className="w-1.5 self-stretch rounded-sm"
                          style={{ backgroundColor: tone.border }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ink">
                            {formatTime12(slot.startTime)} – {formatTime12(slot.endTime)}
                          </div>
                          <div className="text-xs text-muted truncate">
                            {slot.subject?.name || 'All subjects'}
                            {isConsult ? ' · Consultation' : ''}
                          </div>
                        </div>
                        <button onClick={() => onEdit(slot)} className="p-1 text-muted hover:text-ochre">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => onDelete(slot.id)} className="p-1 text-muted hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface SlotModalProps {
  form: SlotFormData;
  setForm: React.Dispatch<React.SetStateAction<SlotFormData>>;
  subjects: Subject[];
  onClose: () => void;
  onSubmit: (e?: React.FormEvent) => void;
  onDelete?: () => void;
  error?: string;
}

const TIME_OPTIONS = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();

const SlotModal: React.FC<SlotModalProps> = ({ form, setForm, subjects, onClose, onSubmit, onDelete, error }) => {
  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-50">
      <div className="bg-paper rounded-card border border-rule w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-rule">
          <h3 className="text-h3 font-serif text-ink">
            {form.id ? 'Edit slot' : 'Add slot'}
          </h3>
          <button type="button" onClick={onClose} className="text-muted hover:text-ink">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-[6px] p-2 text-sm text-red-800">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink mb-1">Day</label>
            <select
              value={form.gridCol}
              onChange={(e) => setForm(f => ({ ...f, gridCol: parseInt(e.target.value) }))}
              className="input"
            >
              {MON_FIRST_LABELS.map((d, i) => (
                <option key={i} value={i}>{d}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink mb-1">Start</label>
              <select
                value={form.startTime}
                onChange={(e) => setForm(f => ({ ...f, startTime: e.target.value }))}
                className="input"
              >
                {TIME_OPTIONS.map(t => (
                  <option key={t} value={t}>{formatTime12(t)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1">End</label>
              <select
                value={form.endTime}
                onChange={(e) => setForm(f => ({ ...f, endTime: e.target.value }))}
                className="input"
              >
                {TIME_OPTIONS.map(t => (
                  <option key={t} value={t}>{formatTime12(t)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">Subject</label>
            <select
              value={form.subjectId}
              onChange={(e) => setForm(f => ({ ...f, subjectId: e.target.value }))}
              className="input"
            >
              <option value="">All subjects</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">Type</label>
            <div className="flex gap-2">
              {(['LESSON', 'CONSULTATION'] as AvailabilityType[]).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, availabilityType: t }))}
                  className={`flex-1 px-3 py-2 rounded-[6px] border text-sm ${
                    form.availabilityType === t
                      ? 'bg-ochre text-white border-ochre'
                      : 'bg-cream text-ink border-rule hover:bg-paper'
                  }`}
                >
                  {t === 'LESSON' ? 'Lesson' : 'Consultation'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {onDelete ? (
              <button
                type="button"
                onClick={onDelete}
                className="inline-flex items-center text-sm text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {form.id ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TutorAvailabilityManager;
